/**
 * Standard Widget Component
 * Reusable widget for displaying related records in detail pages
 */
import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Plus, 
  ExternalLink, 
  ChevronRight,
  Loader2,
  CheckCircle,
  Circle
} from 'lucide-react';

/**
 * Widget Container
 * Standard widget wrapper with header, body, and footer
 */
export const Widget = ({
  title,
  icon: Icon,
  count,
  loading = false,
  onAdd,
  onViewAll,
  addLabel = 'Add',
  viewAllLabel = 'View all',
  emptyMessage = 'No items yet',
  emptyAction,
  children,
  className = ''
}) => {
  const isEmpty = React.Children.count(children) === 0;

  return (
    <div 
      className={`bg-white rounded-lg border shadow-sm ${className}`}
      data-testid="widget-container"
    >
      {/* Widget Header */}
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
          <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
          {typeof count === 'number' && (
            <Badge variant="secondary" className="bg-gray-100 text-gray-600 text-xs">
              {count}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          {onAdd && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onAdd}
              className="h-7 px-2 text-xs"
              data-testid="widget-add-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              {addLabel}
            </Button>
          )}
          {onViewAll && count > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onViewAll}
              className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
              data-testid="widget-view-all-btn"
            >
              {viewAllLabel}
              <ChevronRight className="w-3 h-3 ml-0.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Widget Body */}
      <div className="divide-y" data-testid="widget-body">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : isEmpty ? (
          <div className="py-6 text-center">
            <p className="text-sm text-gray-500 mb-2">{emptyMessage}</p>
            {emptyAction && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={emptyAction.onClick}
                data-testid="widget-empty-action"
              >
                {emptyAction.icon && <emptyAction.icon className="w-3 h-3 mr-1" />}
                {emptyAction.label}
              </Button>
            )}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

/**
 * Widget Item - Basic clickable row
 */
export const WidgetItem = ({
  title,
  subtitle,
  badge,
  badgeColor = 'default',
  rightContent,
  onClick,
  className = ''
}) => {
  const getBadgeColor = (color) => {
    const colors = {
      green: 'bg-green-100 text-green-700',
      blue: 'bg-blue-100 text-blue-700',
      yellow: 'bg-yellow-100 text-yellow-700',
      red: 'bg-red-100 text-red-700',
      purple: 'bg-purple-100 text-purple-700',
      default: 'bg-gray-100 text-gray-600'
    };
    return colors[color] || colors.default;
  };

  return (
    <div
      className={`px-4 py-3 hover:bg-gray-50 transition-colors ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
      data-testid="widget-item"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 truncate mt-0.5">{subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-2 ml-3">
          {badge && (
            <Badge className={`text-xs ${getBadgeColor(badgeColor)}`}>
              {badge}
            </Badge>
          )}
          {rightContent}
          {onClick && (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Widget Contact Item - Specialized for contacts
 */
export const WidgetContactItem = ({
  name,
  email,
  phone,
  title,
  isPrimary,
  onClick,
  className = ''
}) => {
  return (
    <div
      className={`px-4 py-3 hover:bg-gray-50 transition-colors ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
      data-testid="widget-contact-item"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
            {isPrimary && (
              <Badge className="bg-blue-100 text-blue-700 text-xs">Primary</Badge>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            {email && <p className="text-xs text-gray-500 truncate">{email}</p>}
            {phone && <p className="text-xs text-gray-500">{phone}</p>}
          </div>
          {title && <p className="text-xs text-gray-400 mt-0.5">{title}</p>}
        </div>
        {onClick && (
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </div>
    </div>
  );
};

/**
 * Widget Task Item - Specialized for tasks with checkbox
 */
export const WidgetTaskItem = ({
  title,
  dueDate,
  priority,
  status,
  owner,
  onComplete,
  onClick,
  className = ''
}) => {
  const isCompleted = status === 'done';
  const isOverdue = !isCompleted && dueDate && new Date(dueDate) < new Date();
  
  const getPriorityColor = (p) => {
    const colors = {
      high: 'text-red-600',
      med: 'text-yellow-600',
      low: 'text-gray-400'
    };
    return colors[p] || colors.med;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      className={`px-4 py-3 hover:bg-gray-50 transition-colors ${className}`}
      data-testid="widget-task-item"
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onComplete?.();
          }}
          className="mt-0.5 flex-shrink-0"
          data-testid="task-complete-btn"
        >
          {isCompleted ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : (
            <Circle className={`w-4 h-4 ${getPriorityColor(priority)}`} />
          )}
        </button>
        
        {/* Content */}
        <div 
          className={`min-w-0 flex-1 ${onClick ? 'cursor-pointer' : ''}`}
          onClick={onClick}
        >
          <p className={`text-sm font-medium truncate ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
            {title}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            {dueDate && (
              <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                {formatDate(dueDate)}
              </span>
            )}
            {owner && <span className="text-xs text-gray-400">• {owner}</span>}
          </div>
        </div>
        
        {onClick && (
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </div>
    </div>
  );
};

/**
 * Widget Order Item - Specialized for orders
 */
export const WidgetOrderItem = ({
  orderNumber,
  status,
  total,
  dueDate,
  paymentStatus,
  updatedAt,
  onClick,
  className = ''
}) => {
  const getStatusColor = (s) => {
    const colors = {
      new: 'yellow',
      in_production: 'purple',
      ready: 'blue',
      shipped: 'blue',
      closed: 'green',
      cancelled: 'red'
    };
    return colors[s] || 'default';
  };

  const getPaymentColor = (p) => {
    const colors = {
      paid: 'green',
      partial: 'yellow',
      unpaid: 'red'
    };
    return colors[p] || 'default';
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getBadgeClass = (color) => {
    const classes = {
      green: 'bg-green-100 text-green-700',
      blue: 'bg-blue-100 text-blue-700',
      yellow: 'bg-yellow-100 text-yellow-700',
      red: 'bg-red-100 text-red-700',
      purple: 'bg-purple-100 text-purple-700',
      default: 'bg-gray-100 text-gray-600'
    };
    return classes[color] || classes.default;
  };

  return (
    <div
      className={`px-4 py-3 hover:bg-gray-50 transition-colors ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
      data-testid="widget-order-item"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-mono font-medium text-gray-900">{orderNumber}</p>
            <Badge className={`text-xs ${getBadgeClass(getStatusColor(status))}`}>
              {status?.replace('_', ' ')}
            </Badge>
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-gray-600 font-medium">
              ${typeof total === 'number' ? total.toFixed(2) : total}
            </span>
            {dueDate && (
              <span className="text-xs text-gray-500">Due {formatDate(dueDate)}</span>
            )}
            {paymentStatus && (
              <Badge className={`text-xs ${getBadgeClass(getPaymentColor(paymentStatus))}`}>
                {paymentStatus}
              </Badge>
            )}
          </div>
        </div>
        {onClick && (
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </div>
    </div>
  );
};

/**
 * Widget Activity Item - Specialized for activities
 */
export const WidgetActivityItem = ({
  type,
  subject,
  body,
  occurredAt,
  createdBy,
  onClick,
  className = ''
}) => {
  const getTypeIcon = (t) => {
    const icons = {
      call: '📞',
      email: '✉️',
      walk_in: '🚶',
      meeting: '📅',
      note: '📝'
    };
    return icons[t] || '📋';
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      className={`px-4 py-3 hover:bg-gray-50 transition-colors ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
      data-testid="widget-activity-item"
    >
      <div className="flex items-start gap-3">
        <span className="text-base flex-shrink-0">{getTypeIcon(type)}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">
            {subject || type?.replace('_', ' ')}
          </p>
          {body && (
            <p className="text-xs text-gray-500 truncate mt-0.5">{body}</p>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-gray-400">{formatDate(occurredAt)}</span>
            {createdBy && <span className="text-xs text-gray-400">• {createdBy}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Widget;
